/**
 * Audio Control Service for AI Agents
 * Handles TTS, background music, sound effects, and audio management
 */

import { AgentAction } from '../core/types';

export interface AudioTrack {
  id: string;
  name: string;
  url: string;
  type: 'tts' | 'music' | 'sfx' | 'ambient';
  duration: number;
  volume: number;
  loop: boolean;
  category: string;
  emotionalTone?: string;
  situation?: string[];
}

export interface AudioPlaybackState {
  trackId: string;
  isPlaying: boolean;
  currentTime: number;
  volume: number;
  loop: boolean;
  fadeInDuration?: number;
  fadeOutDuration?: number;
}

export interface TTSRequest {
  text: string;
  voice: string;
  speed: number;
  pitch: number;
  emotion?: string;
  style?: string;
  language?: string;
}

export interface SoundEffect {
  id: string;
  name: string;
  type: 'celebration' | 'notification' | 'interaction' | 'ambient' | 'transition';
  url: string;
  volume: number;
  duration: number;
  triggers: string[];
}

export interface VoiceProfile {
  id: string;
  name: string;
  description: string;
  provider: 'openai' | 'alkahest' | 'elevenlabs' | 'azure';
  voiceId: string;
  language: string;
  gender: 'male' | 'female' | 'neutral';
  age: 'child' | 'young' | 'adult' | 'senior';
  characteristics: string[];
  emotionalRange: string[];
  sampleUrl?: string;
}

export interface AudioScene {
  id: string;
  name: string;
  description: string;
  backgroundMusic?: string;
  ambientSounds: string[];
  volume: {
    master: number;
    music: number;
    sfx: number;
    ambient: number;
    tts: number;
  };
  mood: string;
  duration?: number;
}

export class AudioControlService {
  private currentTracks: Map<string, AudioPlaybackState> = new Map();
  private soundEffects: Map<string, SoundEffect> = new Map();
  private voiceProfiles: Map<string, VoiceProfile> = new Map();
  private audioScenes: Map<string, AudioScene> = new Map();
  private currentScene?: AudioScene;
  private masterVolume = 1.0;
  private ttsQueue: TTSRequest[] = [];
  private isProcessingTTS = false;

  constructor() {
    this.initializeDefaultSounds();
    this.initializeVoiceProfiles();
    this.initializeAudioScenes();
  }

  private initializeDefaultSounds(): void {
    const defaultSounds: SoundEffect[] = [
      {
        id: 'tip-notification',
        name: 'Tip Received',
        type: 'notification',
        url: '/audio/sfx/tip-chime.mp3',
        volume: 0.8,
        duration: 2000,
        triggers: ['tip', 'donation', 'support'],
      },
      {
        id: 'goal-completion',
        name: 'Goal Completed',
        type: 'celebration',
        url: '/audio/sfx/goal-fanfare.mp3',
        volume: 0.9,
        duration: 5000,
        triggers: ['goal', 'milestone', 'achievement'],
      },
      {
        id: 'heart-sound',
        name: 'Heart Effect',
        type: 'interaction',
        url: '/audio/sfx/heart-pop.mp3',
        volume: 0.6,
        duration: 1000,
        triggers: ['love', 'heart', 'cute'],
      },
      {
        id: 'sparkle-magic',
        name: 'Magic Sparkles',
        type: 'ambient',
        url: '/audio/sfx/sparkle-magic.mp3',
        volume: 0.4,
        duration: 3000,
        triggers: ['magic', 'sparkle', 'special'],
      },
      {
        id: 'dance-beat',
        name: 'Dance Beat',
        type: 'ambient',
        url: '/audio/sfx/dance-beat.mp3',
        volume: 0.7,
        duration: 8000,
        triggers: ['dance', 'movement', 'energy'],
      },
      {
        id: 'soft-chime',
        name: 'Gentle Chime',
        type: 'transition',
        url: '/audio/sfx/soft-chime.mp3',
        volume: 0.5,
        duration: 2000,
        triggers: ['gentle', 'calm', 'peaceful'],
      },
    ];

    defaultSounds.forEach((sound) => {
      this.soundEffects.set(sound.id, sound);
    });
  }

  private initializeVoiceProfiles(): void {
    const voices: VoiceProfile[] = [
      {
        id: 'anna-sweet',
        name: 'Anna (Sweet)',
        description: 'Warm, friendly female voice with a sweet tone',
        provider: 'openai',
        voiceId: 'alloy',
        language: 'en-US',
        gender: 'female',
        age: 'young',
        characteristics: ['warm', 'friendly', 'sweet', 'energetic'],
        emotionalRange: ['happy', 'excited', 'caring', 'playful'],
      },
      {
        id: 'luna-calm',
        name: 'Luna (Calm)',
        description: 'Soothing female voice perfect for relaxing moments',
        provider: 'openai',
        voiceId: 'nova',
        language: 'en-US',
        gender: 'female',
        age: 'adult',
        characteristics: ['calm', 'soothing', 'mature', 'gentle'],
        emotionalRange: ['peaceful', 'content', 'reassuring', 'wise'],
      },
      {
        id: 'kira-energetic',
        name: 'Kira (Energetic)',
        description: 'High-energy female voice for exciting interactions',
        provider: 'alkahest',
        voiceId: 'kira-v1',
        language: 'en-US',
        gender: 'female',
        age: 'young',
        characteristics: ['energetic', 'bubbly', 'enthusiastic', 'fun'],
        emotionalRange: ['excited', 'joyful', 'surprised', 'dynamic'],
      },
    ];

    voices.forEach((voice) => {
      this.voiceProfiles.set(voice.id, voice);
    });
  }

  private initializeAudioScenes(): void {
    const scenes: AudioScene[] = [
      {
        id: 'chill-lounge',
        name: 'Chill Lounge',
        description: 'Relaxed atmosphere with soft ambient sounds',
        backgroundMusic: '/audio/music/chill-lounge.mp3',
        ambientSounds: ['/audio/ambient/soft-pad.mp3'],
        volume: {
          master: 1.0,
          music: 0.3,
          sfx: 0.8,
          ambient: 0.2,
          tts: 1.0,
        },
        mood: 'relaxed',
      },
      {
        id: 'party-time',
        name: 'Party Time',
        description: 'High-energy party atmosphere',
        backgroundMusic: '/audio/music/party-beats.mp3',
        ambientSounds: ['/audio/ambient/party-crowd.mp3'],
        volume: {
          master: 1.0,
          music: 0.6,
          sfx: 1.0,
          ambient: 0.4,
          tts: 1.0,
        },
        mood: 'energetic',
      },
      {
        id: 'cozy-chat',
        name: 'Cozy Chat',
        description: 'Intimate setting for personal conversations',
        ambientSounds: ['/audio/ambient/fireplace.mp3', '/audio/ambient/rain-soft.mp3'],
        volume: {
          master: 1.0,
          music: 0.1,
          sfx: 0.6,
          ambient: 0.3,
          tts: 1.0,
        },
        mood: 'intimate',
      },
    ];

    scenes.forEach((scene) => {
      this.audioScenes.set(scene.id, scene);
    });
  }

  async processAudioAction(action: AgentAction): Promise<{ success: boolean; data?: any }> {
    try {
      switch (action.action) {
        case 'play':
          return this.playAudio(String(action.value), {
            volume: action.volume || 1.0,
            fadeIn: action.fadeIn,
            fadeOut: action.fadeOut,
          });

        case 'stop':
          return this.stopAudio(String(action.value));

        case 'volume':
          return this.setVolume(String(action.value), action.volume || 1.0);

        case 'effect':
          return this.playEffect(String(action.value));

        default:
          return { success: false, data: { error: `Unknown audio action: ${action.action}` } };
      }
    } catch (error) {
      return {
        success: false,
        data: { error: error instanceof Error ? error.message : 'Audio processing failed' },
      };
    }
  }

  async generateTTS(
    request: TTSRequest
  ): Promise<{ success: boolean; audioUrl?: string; duration?: number }> {
    try {
      // Add to queue for processing
      this.ttsQueue.push(request);

      if (!this.isProcessingTTS) {
        this.processTTSQueue();
      }

      // In a real implementation, this would interface with TTS providers
      const mockAudioUrl = `/audio/tts/generated_${Date.now()}.mp3`;
      const estimatedDuration = request.text.length * 100; // Rough estimate

      return {
        success: true,
        audioUrl: mockAudioUrl,
        duration: estimatedDuration,
      };
    } catch (_error) {
      return { success: false };
    }
  }

  private async processTTSQueue(): Promise<void> {
    this.isProcessingTTS = true;

    while (this.ttsQueue.length > 0) {
      const request = this.ttsQueue.shift()!;

      // Process TTS request with the actual request data
      // In real implementation, this would call the actual TTS provider
      console.log('Processing TTS:', request.text);
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    this.isProcessingTTS = false;
  }

  playAudio(
    trackId: string,
    options: {
      volume?: number;
      loop?: boolean;
      fadeIn?: number;
      fadeOut?: number;
    } = {}
  ): { success: boolean; data?: any } {
    try {
      const playbackState: AudioPlaybackState = {
        trackId,
        isPlaying: true,
        currentTime: 0,
        volume: options.volume || 1.0,
        loop: options.loop || false,
        fadeInDuration: options.fadeIn,
        fadeOutDuration: options.fadeOut,
      };

      this.currentTracks.set(trackId, playbackState);

      return { success: true, data: { playbackState } };
    } catch (_error) {
      return { success: false, data: { error: 'Failed to play audio' } };
    }
  }

  stopAudio(trackId: string): { success: boolean; data?: any } {
    const playbackState = this.currentTracks.get(trackId);
    if (!playbackState) {
      return { success: false, data: { error: 'Track not found' } };
    }

    playbackState.isPlaying = false;
    this.currentTracks.delete(trackId);

    return { success: true, data: { stopped: trackId } };
  }

  playEffect(effectId: string): { success: boolean; data?: any } {
    const effect = this.soundEffects.get(effectId);
    if (!effect) {
      return { success: false, data: { error: `Sound effect not found: ${effectId}` } };
    }

    return this.playAudio(effectId, {
      volume: effect.volume,
      loop: false,
    });
  }

  setVolume(trackId: string, volume: number): { success: boolean; data?: any } {
    const playbackState = this.currentTracks.get(trackId);
    if (!playbackState) {
      return { success: false, data: { error: 'Track not found' } };
    }

    playbackState.volume = Math.max(0, Math.min(1, volume));
    return { success: true, data: { volume: playbackState.volume } };
  }

  setMasterVolume(volume: number): void {
    this.masterVolume = Math.max(0, Math.min(1, volume));
  }

  getMasterVolume(): number {
    return this.masterVolume;
  }

  setAudioScene(sceneId: string): { success: boolean; data?: any } {
    const scene = this.audioScenes.get(sceneId);
    if (!scene) {
      return { success: false, data: { error: `Audio scene not found: ${sceneId}` } };
    }

    // Stop current scene
    if (this.currentScene) {
      this.stopScene(this.currentScene.id);
    }

    this.currentScene = scene;

    // Start background music if specified
    if (scene.backgroundMusic) {
      this.playAudio(scene.backgroundMusic, {
        volume: scene.volume.music,
        loop: true,
      });
    }

    // Start ambient sounds
    scene.ambientSounds.forEach((ambientUrl, index) => {
      this.playAudio(`ambient_${index}`, {
        volume: scene.volume.ambient,
        loop: true,
      });
      console.log('Started ambient sound:', ambientUrl);
    });

    return { success: true, data: { scene: scene.name } };
  }

  private stopScene(sceneId: string): void {
    console.log('Stopping scene:', sceneId);
    // Stop all current tracks
    this.currentTracks.forEach((_, trackId) => {
      this.stopAudio(trackId);
    });
  }

  getCurrentPlayingTracks(): AudioPlaybackState[] {
    return Array.from(this.currentTracks.values()).filter((state) => state.isPlaying);
  }

  getAvailableVoices(): VoiceProfile[] {
    return Array.from(this.voiceProfiles.values());
  }

  getVoiceByEmotion(emotion: string): VoiceProfile | null {
    for (const voice of this.voiceProfiles.values()) {
      if (voice.emotionalRange.includes(emotion)) {
        return voice;
      }
    }
    return null;
  }

  getSoundEffectsByTrigger(trigger: string): SoundEffect[] {
    return Array.from(this.soundEffects.values()).filter((effect) =>
      effect.triggers.includes(trigger)
    );
  }

  getAudioScenes(): AudioScene[] {
    return Array.from(this.audioScenes.values());
  }

  getCurrentScene(): AudioScene | null {
    return this.currentScene || null;
  }

  createDynamicAudioResponse(
    emotion: string,
    context: string,
    intensity: number = 0.5
  ): AgentAction[] {
    const actions: AgentAction[] = [];

    // Select appropriate voice based on emotion
    const voice = this.getVoiceByEmotion(emotion);
    if (voice) {
      // This would be used for TTS generation
    }

    // Add sound effects based on context and emotion
    const contextEffects = this.getSoundEffectsByTrigger(context);
    if (contextEffects.length > 0) {
      const selectedEffect = contextEffects[0];
      actions.push({
        type: 'audio',
        action: 'effect',
        value: selectedEffect.id,
        volume: selectedEffect.volume * intensity,
      });
    }

    // Add ambient audio based on emotion
    if (emotion === 'excited' && intensity > 0.7) {
      actions.push({
        type: 'audio',
        action: 'play',
        value: 'dance-beat',
        volume: 0.3 * intensity,
        fadeIn: 1000,
      });
    } else if (emotion === 'calm' || emotion === 'peaceful') {
      actions.push({
        type: 'audio',
        action: 'play',
        value: 'soft-chime',
        volume: 0.2 * intensity,
      });
    }

    return actions;
  }

  analyzeAudioMood(currentTracks: string[]): string {
    // Analyze currently playing tracks to determine overall mood
    let energyLevel = 0;

    currentTracks.forEach((trackId) => {
      const effect = this.soundEffects.get(trackId);
      if (effect) {
        switch (effect.type) {
          case 'celebration':
            energyLevel += 0.3;
            break;
          case 'ambient':
            energyLevel += 0.1;
            break;
          case 'interaction':
            energyLevel += 0.2;
            break;
        }
      }
    });

    if (energyLevel > 0.5) {
      return 'energetic';
    } else if (energyLevel > 0.2) {
      return 'moderate';
    } else {
      return 'calm';
    }
  }

  queueTTS(text: string, voice?: string, emotion?: string): string {
    const request: TTSRequest = {
      text,
      voice: voice || 'anna-sweet',
      speed: 1.0,
      pitch: 1.0,
      emotion,
    };

    this.ttsQueue.push(request);

    if (!this.isProcessingTTS) {
      this.processTTSQueue();
    }

    return `tts_${Date.now()}`;
  }

  stopAllAudio(): void {
    this.currentTracks.clear();
  }

  crossfade(fromTrackId: string, toTrackId: string, duration: number = 2000): void {
    // Implement crossfade between tracks
    const fromTrack = this.currentTracks.get(fromTrackId);
    if (fromTrack) {
      fromTrack.fadeOutDuration = duration;
    }

    this.playAudio(toTrackId, { fadeIn: duration });
  }
}
